import { unreadable } from './shared.ts';

/**
 * §8's Playlist page, and the editing half from 3c.
 *
 * The page carries no notices at all. The four that reported on the response — rows without a byline,
 * a count that disagreed with the list, pages that overlapped and were folded — told the reader about
 * our plumbing and went first. The fifth counted the entries YouTube no longer serves, and it went
 * too (2026-08-07): every one of those rows already says so on itself, in place and next to the
 * number it explains, so a banner over the list only repeated a count of what was on screen.
 * `entryGone` below is that per-row label, and it stays.
 */
export const playlist = {
  entry: {
    /** Stands in for a title the endpoint returned empty, so the remove button still names its row. */
    positionLabel: (position: number): string => `entry ${position}`,
    upcoming: 'Upcoming',
  },

  empty: {
    title: 'This playlist has no videos',
    /** The normal state right after creating one — so the header and its controls stay on screen. */
    owned:
      'Nothing has been added to it yet. Adding a video is done from the video: open it and use "Save to playlist".',
    mirrored:
      'YouTube has this playlist and nothing in it. A playlist whose every video has been removed looks the same.',
  },

  /** The page itself — the header, the editor, the numbered list and its paging. */
  page: {
    loading: 'Loading this playlist…',
    views: (count: string): string => `${count} views`,
    /** 4e's second named victim of the mixed-locale sentence — see `playlists.page.updated`. */
    updated: (when: string): string => `Updated ${when}`,
    onThisInstance: 'On this instance',
    playAll: 'Play all',
    /** One word: it sits beside Play all and Manage, which already say whose page this is. */
    edit: 'Edit',
    close: 'Close',
    entriesLabel: 'Playlist entries',
    /**
     * The display order. "Original", because "as added" would be a claim about mirrored
     * playlists nothing verifies — YouTube curators reorder freely.
     */
    order: {
      label: 'Order',
      normal: 'Original',
      reversed: 'Reversed',
    },
    remove: (title: string): string => `Remove “${title}” from this playlist`,
    /** The corner button's hover word — the act alone; the card states the title itself. */
    removeTitle: 'Remove from this playlist',
    removing: 'Removing…',
    /** A row the endpoint blanked. Not a link: the id is real, so an anchor would look live. */
    entryGone: 'Video unavailable',
    entryGoneDetail: 'Deleted, made private, or from a closed account',
    loadMore: (soFar: number): string => `Load more (${soFar} so far)`,
    loadingMore: 'Loading…',
  },

  /** Manage mode: the add box, the selection, and the three acts over it. */
  manage: {
    enter: 'Manage',
    leave: 'Done',
    selectLabel: (title: string): string => `Select ${title}`,
    add: {
      heading: 'Add videos',
      label: 'Find a video to add',
      placeholder: 'A video URL or ID, or a search',
      clear: 'Clear the box',
      submit: 'Find',
      searching: 'Searching…',
      nothing: (q: string): string => `No video matches “${q}”.`,
      notAvailable:
        'That points at a video YouTube will not serve, whether deleted, private or region-blocked, so there is nothing to add.',
      action: 'Add',
      added: 'Added',
      failed: 'Failed',
    },
    /** The selection's size, said once beside the way out of it — never inside the action buttons,
        which name their act and nothing else. */
    selectedCount: (count: number): string => `${count} selected`,
    clearSelection: 'Clear selection',
    removeSelected: 'Remove',
    copySelected: 'Copy to…',
    moveSelected: 'Move to…',
    working: (done: number, total: number): string => `Working… ${done} of ${total}`,
    failed: {
      title: 'Some changes were refused',
      detail:
        'The instance did not accept part of the change. The list shows what it holds now, and selecting the leftovers and trying again is safe. A video YouTube no longer serves cannot be copied, only removed.',
      retryable: true,
    },
    transfer: {
      copyTitle: 'Copy to playlist',
      moveTitle: 'Move to playlist',
      close: 'Close',
      loading: 'Loading your playlists…',
      none: 'No other playlist yet. The first one is a title away.',
      copyCreate: 'Create & copy',
      moveCreate: 'Create & move',
    },
  },

  editor: {
    sectionLabel: 'Edit this playlist',
    titleLabel: 'Title',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'What this playlist is for',
    privacyLabel: 'Who can see it',
    /** Nothing is preselected, and that is honest: unlisted and private arrive here as one value. */
    currently: (label: string, caveat: string): string => `Currently ${label}. ${caveat}`,
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    /** Two clicks (`ConfirmButton`): deletion has no undo, and the armed title says so. */
    delete: 'Delete playlist',
    confirmDelete: 'Deletes this playlist for good. There is no undo.',
    deleting: 'Deleting…',
    noReorder:
      'Entries stay in the order they were added. There is no way to move one except to remove it and add it again.',
  },

  problem: {
    /**
     * Covers **three different 404 bodies** that all mean the same thing — a relayed YouTube 404 for an
     * unknown `PL…` (and for `WL`/`LL`, which need an account), a body about a missing sidebar for
     * something that is not a playlist id at all, and a flat refusal for an unknown `IVPL…`.
     */
    notFound: {
      title: 'No such playlist',
      detail:
        'Either it does not exist, or it is private. A private playlist is invisible to everyone but its owner, signed in or not. Watch Later and Liked videos answer this way too: they belong to one account and have no public form.',
      retryable: false,
    },
    unreadable: unreadable('Could not read this playlist'),
    /** A YouTube **mix** (`RDCLAK5uy_…`) fails upstream, and mixes are linkable. */
    upstreamBroken: {
      title: 'This is not a playlist Visious can open',
      detail:
        'YouTube would not serve it. A mix or radio playlist is generated fresh for each viewer instead of being stored, and there is no page for one here. Trying again gets the same answer.',
      retryable: false,
    },
  },
} as const;
